import React, { useState } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useApiClient } from '../../hooks/useApiClient'

interface ComprobanteViewerProps {
  pagoId: number
  filename?: string
  mimeType?: string
  size?: number
  uploadDate?: string
  showMetadata?: boolean
}

const ComprobanteViewer: React.FC<ComprobanteViewerProps> = ({
  pagoId,
  filename = 'comprobante',
  mimeType = 'application/octet-stream',
  size,
  uploadDate,
  showMetadata = true,
}) => {
  const { colors } = useTheme()
  const { apiClient } = useApiClient()
  const [isLoading, setIsLoading] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Detección robusta de tipos MIME (ignorar mayúsculas y espacios)
  const normalizedMime = mimeType?.toLowerCase().trim() ?? ''
  const isImage = normalizedMime.startsWith('image/')
  const isPdf = normalizedMime === 'application/pdf' || normalizedMime.includes('pdf')

  const handlePreview = async () => {
    if (!isImage && !isPdf) return

    setIsLoading(true)
    try {
      const response = await apiClient.get(
        `/api/admin-protected/pagos/${pagoId}/descargar-comprobante`,
        { responseType: 'blob' }
      )
      const blob = response.data
      const url = window.URL.createObjectURL(blob)
      setPreviewUrl(url)
      setIsPreviewOpen(true)
    } catch (error) {
      console.error('Error loading preview:', error)
      toastError('Error al cargar la vista previa')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.get(
        `/api/admin-protected/pagos/${pagoId}/descargar-comprobante`,
        { responseType: 'blob' }
      )
      const blob = response.data
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toastSuccess('Descargado exitosamente')
    } catch (error) {
      console.error('Error downloading comprobante:', error)
      toastError('Error al descargar el comprobante')
    } finally {
      setIsLoading(false)
    }
  }

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl)
    }
    setIsPreviewOpen(false)
    setPreviewUrl(null)
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const formatDate = (date?: string) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <>
      <div className="space-y-3">
        {/* File Info */}
        {showMetadata && (
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500">Archivo</p>
                <p className="text-xs font-medium text-gray-900 truncate">{filename || 'Sin nombre'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Tamaño</p>
                <p className="text-xs font-medium text-gray-900">{formatFileSize(size)}</p>
              </div>
              {uploadDate && (
                <div>
                  <p className="text-xs text-gray-500">Cargado</p>
                  <p className="text-xs font-medium text-gray-900">{formatDate(uploadDate)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {isImage && (
            <button
              onClick={handlePreview}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: colors?.primary || '#007bff',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = isLoading ? '0.5' : '1'
              }}
            >
              {isLoading ? '⏳...' : '👁️ Ver'}
            </button>
          )}

          {isPdf && (
            <button
              onClick={handlePreview}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: colors?.primary || '#007bff',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = isLoading ? '0.5' : '1'
              }}
            >
              {isLoading ? '⏳...' : '👁️ Ver PDF'}
            </button>
          )}

          <button
            onClick={handleDownload}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'white',
              backgroundColor: colors?.primary || '#007bff',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.opacity = isLoading ? '0.5' : '1'
            }}
          >
            {isLoading ? '⏳...' : '⬇️ Descargar'}
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {isPreviewOpen && isImage && previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">{filename}</h2>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex items-center justify-center">
              <img
                src={previewUrl}
                alt={filename}
                className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end bg-gray-50">
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: colors.primary,
                }}
              >
                ⬇️ Descargar
              </button>
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal (display embedded) */}
      {isPreviewOpen && isPdf && previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">{filename}</h2>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex items-center justify-center">
              <embed
                src={previewUrl}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: 'calc(90vh - 180px)',
                  borderRadius: '8px',
                }}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end bg-gray-50">
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: colors.primary,
                }}
              >
                ⬇️ Descargar
              </button>
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ComprobanteViewer
